import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Card,
  Form,
  Select,
  Input,
  Slider,
  Button,
  Row,
  Col,
  Typography,
  Space,
  Tooltip,
  Modal,
  Tag,
  Transfer,
  Alert,
  Descriptions,
  Divider,
  message,
  Tour,
} from 'antd';
import {
  DatabaseOutlined,
  SettingOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  ExportOutlined,
  FireOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import {
  v2AnalysisApi,
  v2StylesApi,
  v2ToppingsApi,
  creditsApi,
  gamificationApi,
} from '../../../../api';
import { getUserProfile } from '../../../../auth';
import yaml from 'js-yaml';
import './generation.css';
import StyleOverrideModal from './StyleOverrideModal';
import TipsRotator from '../../../Common/TipsRotator';
import { generationStep1Tips } from '../../../Common/tipsData';
import {
  calculateEstimatedUsageCredits,
  calculateTotalCredits,
  BASE_GENERATION_CREDITS,
} from '../../../../utils/creditCalculation';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTutorialTour } from '../../../../hooks/useTutorialTour';
import { TOUR_CONFIGS } from '../../../../config/tourConfigs';
import { LockOutlined } from '@ant-design/icons';
import EnhancedTour from '../../../Common/EnhancedTour';

const Step1BasicDesign = ({ initialValues, onComplete, onValuesChange, isGuest = false, onGuestAction = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const preSelectedAnalysisId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('analysisId') || initialValues?.selectedAnalysisId || null;
  }, [location.search, initialValues?.selectedAnalysisId]);
  const [form] = Form.useForm();
  const selectedAnalysisId = Form.useWatch('selectedAnalysisId', form);
  const selectedStyleIdWatch = Form.useWatch('selectedStyleId', form);

  // ツアー管理
  const {
    tourOpen,
    completeTour,
    skipTour,
    restartTour,
    currentStep,
    updateCurrentStep,
    loading: tourLoading,
    isMicroMode,
  } = useTutorialTour('generation_step1', {
    microKey: 'generation_step1_micro',
    disabled: isGuest,
  });

  // 親コンポーネント（Generation/index.js）からのツアー再表示イベントをリッスン
  useEffect(() => {
    const handleRestartTour = () => {
      restartTour();
    };
    window.addEventListener('restartGenerationTour', handleRestartTour);
    return () => {
      window.removeEventListener('restartGenerationTour', handleRestartTour);
    };
  }, [restartTour]);

  const [loading, setLoading] = useState({
    analyses: false,
    styles: false,
    toppings: false,
  });
  const [analysisOptions, setAnalysisOptions] = useState([]);
  const [allAnalysisOptions, setAllAnalysisOptions] = useState([]); // 検索用：全件保持
  const [styleOptions, setStyleOptions] = useState([]);
  const [stylesRaw, setStylesRaw] = useState([]);
  const [defaultStyleId, setDefaultStyleId] = useState(null);
  const [toppingOptions, setToppingOptions] = useState([]);
  const [toppingModalOpen, setToppingModalOpen] = useState(false);
  const [toppingTargetKeys, setToppingTargetKeys] = useState([]);
  const selectedAnalysisIdWatch = Form.useWatch('selectedAnalysisId', form);
  const [hasAnalysis, setHasAnalysis] = useState(false);

  // 🔥 クレジット確認モーダル
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [confirming, setConfirming] = useState(false);

  // スタイル微調整用のstate（refで確実に保持）
  const [styleOverrides, setStyleOverrides] = useState(null);
  const styleOverridesRef = useRef(null);
  const [styleOverrideModalOpen, setStyleOverrideModalOpen] = useState(false);

  // スタイル微調整機能のロック状態
  const [styleAdjustmentUnlocked, setStyleAdjustmentUnlocked] = useState(false);
  const [styleAdjustmentRequiredPlan, setStyleAdjustmentRequiredPlan] =
    useState('');

  // カスタムスタイル機能のプラン制限
  const [customStyleUnlocked, setCustomStyleUnlocked] = useState(false);
  const [customStyleMaxLimit, setCustomStyleMaxLimit] = useState(0);

  // リサーチ検索デバウンス用
  const searchTimerRef = useRef(null);

  // スタイル微調整のサマリーを取得（差分のみ、値も表示）
  const getOverrideSummary = overrides => {
    if (!overrides || Object.keys(overrides).length === 0) return [];
    const summary = [];

    // 見出し設定
    const headings = overrides?.headings_style;
    if (headings) {
      if (headings.counts?.h2_per_article?.typical) {
        summary.push(`H2数: ${headings.counts.h2_per_article.typical}個`);
      }
      if (headings.counts?.h3_per_h2?.typical) {
        summary.push(`H3数: ${headings.counts.h3_per_h2.typical}個/H2`);
      }
      if (headings.length_policy?.h2_chars?.typical) {
        summary.push(
          `H2文字数: ${headings.length_policy.h2_chars.typical}文字`,
        );
      }
      if (headings.length_policy?.h3_chars?.typical) {
        summary.push(
          `H3文字数: ${headings.length_policy.h3_chars.typical}文字`,
        );
      }
      if (headings.length_policy?.h2_body_chars?.typical) {
        summary.push(
          `H2本文: ${headings.length_policy.h2_body_chars.typical}文字`,
        );
      }
      if (headings.length_policy?.h3_body_chars?.typical) {
        summary.push(
          `H3本文: ${headings.length_policy.h3_body_chars.typical}文字`,
        );
      }
      if (headings.temperature?.allow_interrogative !== undefined) {
        summary.push(
          `疑問文: ${headings.temperature.allow_interrogative ? '使用する' : '使用しない'}`,
        );
      }
      if (headings.temperature?.allow_symbols) {
        const symbols = [];
        if (headings.temperature.allow_symbols.question) symbols.push('？');
        if (headings.temperature.allow_symbols.brackets) symbols.push('()[]');
        if (headings.temperature.allow_symbols.colon) symbols.push(':');
        summary.push(`許可記号: ${symbols.join(', ') || 'なし'}`);
      }
    }

    // 装飾 - prose_style構造に対応
    const proseStyle = overrides?.prose_style;
    if (proseStyle) {
      const presetLabels = {
        very_low: 'ほとんどなし',
        low: '少なめ',
        slightly_low: 'やや少なめ',
        medium: '標準',
        slightly_high: 'やや多め',
        high: '多め',
        very_high: 'かなり多め',
      };

      // プリセット名→数値範囲の逆引き用マッピング
      const getRangeLabel = range => {
        if (!range) return '';
        // 数値範囲をプリセット名に変換
        const mid = range.includes('-')
          ? (parseInt(range.split('-')[0]) + parseInt(range.split('-')[1])) / 2
          : parseInt(range);

        if (mid < 1) return presetLabels.very_low;
        if (mid < 2) return presetLabels.low;
        if (mid <= 6) return presetLabels.medium;
        if (mid <= 8) return presetLabels.slightly_high;
        if (mid <= 10) return presetLabels.high;
        return presetLabels.very_high;
      };

      if (proseStyle.formatting?.bullets?.frequency_per_1000w?.typical) {
        const label = getRangeLabel(
          proseStyle.formatting.bullets.frequency_per_1000w.typical,
        );
        summary.push(`箇条書き頻度: ${label}`);
      }
      if (proseStyle.formatting?.tables?.frequency_per_1000w?.typical) {
        const label = getRangeLabel(
          proseStyle.formatting.tables.frequency_per_1000w.typical,
        );
        summary.push(`表頻度: ${label}`);
      }
      if (proseStyle.formatting?.rules?.bullets?.max_nesting !== undefined) {
        summary.push(
          `箇条書き入れ子: ${proseStyle.formatting.rules.bullets.max_nesting}段階`,
        );
      }
      if (proseStyle.formatting?.rules?.bullets?.item_max_chars !== undefined) {
        summary.push(
          `箇条書き項目文字数: ${proseStyle.formatting.rules.bullets.item_max_chars}文字`,
        );
      }
      if (
        proseStyle.formatting?.rules?.bullets?.terminal_punct_disallowed !==
        undefined
      ) {
        summary.push(
          `箇条書き句点: ${proseStyle.formatting.rules.bullets.terminal_punct_disallowed ? '付けない' : '付ける'}`,
        );
      }
      if (
        proseStyle.formatting?.rules?.bullets?.parallelism_required !==
        undefined
      ) {
        summary.push(
          `箇条書き並列性: ${proseStyle.formatting.rules.bullets.parallelism_required ? '揃える' : '揃えない'}`,
        );
      }

      // カスタムルール - prose_style.constraints構造に対応
      if (proseStyle.constraints?.additional_instructions_outline) {
        summary.push(`構成案追加指示: あり`);
      }
      if (proseStyle.constraints?.additional_instructions_content) {
        summary.push(`本文追加指示: あり`);
      }
      if (
        proseStyle.constraints?.avoid_patterns &&
        proseStyle.constraints.avoid_patterns.length > 0
      ) {
        summary.push(
          `禁止表現: ${proseStyle.constraints.avoid_patterns.length}件`,
        );
      }
      if (
        proseStyle.constraints?.rewrite_rules &&
        proseStyle.constraints.rewrite_rules.length > 0
      ) {
        summary.push(
          `置換ルール: ${proseStyle.constraints.rewrite_rules.length}件`,
        );
      }
    }

    // 調査フィルター
    const domainFilter = overrides?.domain_filter_override;
    if (domainFilter && domainFilter.mode && domainFilter.mode !== 'disabled') {
      const modeLabel =
        domainFilter.mode === 'whitelist' ? 'ホワイトリスト' : 'ブラックリスト';
      const list =
        domainFilter.mode === 'whitelist'
          ? domainFilter.whitelist
          : domainFilter.blacklist;
      const count = Array.isArray(list) ? list.length : 0;
      summary.push(`調査フィルター: ${modeLabel}（${count}件）`);
    }

    return summary;
  };

  const openExternal = url => {
    try {
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      if (w && typeof w === 'object') {
        w.opener = null;
        return;
      }
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (_) {
      window.location.href = url;
    }
  };

  // Tourステップ定義（micro/fullを切り替え）
  const tourSteps = useMemo(() => {
    const configKey = isMicroMode
      ? 'generation_step1_micro'
      : 'generation_step1';
    if (tourLoading || !TOUR_CONFIGS[configKey]) return [];
    return TOUR_CONFIGS[configKey].steps.map(step => ({
      ...step,
      target: step.target || null,
    }));
  }, [tourLoading, isMicroMode]);

  // リサーチ結果の検索処理（サーバーサイド検索）
  const handleSearchAnalysis = searchValue => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (!searchValue || searchValue.trim() === '') {
      setAnalysisOptions(allAnalysisOptions);
      return;
    }

    // まずクライアント側で即時フィルタ（UX向上）
    const filtered = allAnalysisOptions.filter(option => {
      const s = searchValue.toLowerCase();
      return (option.label || '').toLowerCase().includes(s) ||
             (option.keyword || '').toLowerCase().includes(s);
    });
    setAnalysisOptions(filtered);

    // 500ms後にサーバーサイド検索（より多くの結果を取得）
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await v2AnalysisApi.listAnalyses({
          limit: 50,
          q: searchValue.trim(),
          status: 'completed',
        });
        const list = Array.isArray(res) ? res : res?.analyses || [];
        const completedList = list.filter(
          a => a.status === 'completed' || a.status === 'completed_with_errors',
        );
        const serverItems = completedList.map(a => ({
          value: a.analysis_id,
          label: `${a.keyword}（${(a.created_at || '').toString().slice(0, 10)}）`,
          keyword: a.keyword,
        }));
        // クライアント側の結果とマージし重複排除
        const merged = [...allAnalysisOptions, ...serverItems];
        const unique = merged.filter(
          (item, idx, arr) => arr.findIndex(x => x.value === item.value) === idx,
        );
        const finalFiltered = unique.filter(option => {
          const s = searchValue.toLowerCase();
          return (option.label || '').toLowerCase().includes(s) ||
                 (option.keyword || '').toLowerCase().includes(s);
        });
        setAnalysisOptions(finalFiltered);
      } catch (_) {
        // サーバー検索失敗時はクライアント側の結果を維持
      }
    }, 500);
  };

  // スタイル微調整モーダルの保存ハンドラー
  const handleSaveStyleOverrides = overrides => {
    setStyleOverrides(overrides);
    styleOverridesRef.current = overrides; // refにも保存
    setStyleOverrideModalOpen(false);
  };

  useEffect(() => {
    const loadAnalyses = async () => {
      setLoading(prev => ({ ...prev, analyses: true }));
      try {
        const res = await v2AnalysisApi.listAnalyses({ limit: 100 });
        const list = Array.isArray(res) ? res : res?.analyses || [];
        // 失敗・エラー・中断ステータスを除外（完了のみ表示）
        const completedList = list.filter(
          a => a.status === 'completed' || a.status === 'completed_with_errors',
        );
        const items = completedList.map(a => ({
          value: a.analysis_id,
          label: `${a.keyword}（${(a.created_at || '').toString().slice(0, 10)}）`,
          keyword: a.keyword,
        }));
        setAnalysisOptions(items);
        setAllAnalysisOptions(items);
      } catch (_) {
        const demoItems = [
          {
            value: 'demo-1',
            label: 'SEO AEO 違い（2025-09-04）',
            keyword: 'SEO AEO 違い',
          },
          {
            value: 'demo-2',
            label: 'E-E-A-T とは（2025-09-01）',
            keyword: 'E-E-A-T とは',
          },
        ];
        setAnalysisOptions(demoItems);
        setAllAnalysisOptions(demoItems);
      } finally {
        setLoading(prev => ({ ...prev, analyses: false }));
      }
    };

    const loadStyles = async () => {
      setLoading(prev => ({ ...prev, styles: true }));
      let list = [];
      try {
        const res = await v2StylesApi.getStyles({ params: { limit: 50 } });
        list = res?.items || res || [];
        // DBから取得したスタイル（デフォルトスタイルを含む）を使用
        setStylesRaw(list);
        const items = list.map(s => {
          const memoText = (s.memo || s.note || s.description || '')
            .toString()
            .slice(0, 40)
            .replace(/\s+/g, ' ')
            .trim();
          const name = s.name || `Style #${s.id}`;
          return {
            value: s.id,
            label: memoText ? `${name} — ${memoText}` : name,
          };
        });
        setStyleOptions(items);

        // デフォルトスタイルを見つけてstateに保存
        const defaultStyle = list.find(s => s.is_default === true);
        const defaultId = defaultStyle
          ? defaultStyle.id
          : list.length > 0
            ? list[0].id
            : null;

        if (defaultId) {
          setDefaultStyleId(defaultId);
          // フォームの現在値がない場合のみ設定
          const cur = form.getFieldValue('selectedStyleId');
          if (!cur) {
            form.setFieldsValue({ selectedStyleId: defaultId });
          }
        }
      } catch (err) {
        // エラー時はログ出力
        console.error('[STEP1] Failed to load styles:', err);
        // 空の選択肢を設定
        setStyleOptions([]);
        setStylesRaw([]);
      } finally {
        setLoading(prev => ({ ...prev, styles: false }));
      }
    };

    const loadToppings = async () => {
      setLoading(prev => ({ ...prev, toppings: true }));
      try {
        const res = await v2ToppingsApi.getToppings();
        const list = res?.data?.items || res?.items || res || [];
        const items = list.map(t => ({
          value: String(t.id),
          label: t.title || t.name || `Doc #${t.id}`,
          type: t.type || t.content_type || null,
          category: t.category || t.category_name || null,
        }));
        setToppingOptions(items);
      } catch (_) {
        setToppingOptions([
          {
            value: '201',
            label: '自社サービス資料（最新版）',
            type: '資料',
            category: '製品',
          },
          {
            value: '202',
            label: '顧客調査まとめ（2024Q4）',
            type: '調査',
            category: '顧客',
          },
          {
            value: '203',
            label: 'CTAテンプレ集',
            type: 'テンプレ',
            category: 'マーケ',
          },
        ]);
      } finally {
        setLoading(prev => ({ ...prev, toppings: false }));
      }
    };

    if (!isGuest) {
      loadAnalyses();
      loadStyles();
      loadToppings();
    } else {
      const loadGuestData = async () => {
        try {
          const { publicSamplesApi } = await import('../../../../api');
          const samples = await publicSamplesApi.getSampleAnalysisList();
          const analyses = samples?.analyses || samples || [];
          const items = analyses.map(a => ({
            value: a.analysis_id,
            label: `${a.keyword}（サンプル）`,
            keyword: a.keyword,
          }));
          setAnalysisOptions(items);
          setAllAnalysisOptions(items);
        } catch (e) {
          console.error('[STEP1] Failed to load guest analyses:', e);
        }
        try {
          const { publicSamplesApi } = await import('../../../../api');
          const defaultStyle = await publicSamplesApi.getDefaultStyle();
          if (defaultStyle) {
            const styleItem = { value: defaultStyle.id, label: defaultStyle.name || 'magicss-default' };
            setStyleOptions([styleItem]);
            setStylesRaw([defaultStyle]);
            form.setFieldsValue({ selectedStyleId: defaultStyle.id });
          }
        } catch (e) {
          console.error('[STEP1] Failed to load default style:', e);
          const defaultStyleItem = { value: 'default', label: 'magicss-default' };
          setStyleOptions([defaultStyleItem]);
          form.setFieldsValue({ selectedStyleId: 'default' });
        }
      };
      loadGuestData();
    }
    const initId = form.getFieldValue('selectedAnalysisId');
    setHasAnalysis(!!initId);
  }, [isGuest]);

  // モーダルを開いたタイミングでフォームの値を同期して即時反映
  useEffect(() => {
    if (toppingModalOpen) {
      const current = form.getFieldValue('selectedToppingIds') || [];
      setToppingTargetKeys(current.map(String));
    }
  }, [toppingModalOpen, form]);

  // リサーチ結果一覧読み込み後、遷移元から渡された初期選択を反映
  const preSelectionApplied = useRef(false);
  useEffect(() => {
    if (
      allAnalysisOptions.length > 0 &&
      preSelectedAnalysisId &&
      !preSelectionApplied.current
    ) {
      const match = allAnalysisOptions.find(
        o => o.value === preSelectedAnalysisId,
      );
      if (match) {
        preSelectionApplied.current = true;
        form.setFieldsValue({
          selectedAnalysisId: preSelectedAnalysisId,
          keyword: match.keyword,
        });
        setHasAnalysis(true);
      }
    }
  }, [allAnalysisOptions, preSelectedAnalysisId]);

  // プラン制限に応じてスタイル選択肢をフィルタリング
  useEffect(() => {
    if (isGuest || stylesRaw.length === 0) return;

    const defaultStyles = stylesRaw.filter(s => s.is_default === true);
    const customStyles = stylesRaw.filter(s => !s.is_default);

    let allowedStyles;
    if (!customStyleUnlocked) {
      // フリープラン: デフォルトのみ
      allowedStyles = defaultStyles;
    } else if (customStyleMaxLimit > 0 && customStyleMaxLimit < 999) {
      // ライトプラン等: デフォルト + 最新N件のカスタムスタイル
      const sorted = [...customStyles].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );
      allowedStyles = [...defaultStyles, ...sorted.slice(0, customStyleMaxLimit)];
    } else {
      allowedStyles = stylesRaw;
    }

    const items = allowedStyles.map(s => {
      const memoText = (s.memo || s.note || s.description || '')
        .toString().slice(0, 40).replace(/\s+/g, ' ').trim();
      const name = s.name || `Style #${s.id}`;
      return {
        value: s.id,
        label: memoText ? `${name} — ${memoText}` : name,
      };
    });
    setStyleOptions(items);

    // 現在選択中のスタイルが許可リストにない場合、デフォルトにリセット
    const currentId = form.getFieldValue('selectedStyleId');
    if (currentId && !allowedStyles.find(s => s.id === currentId)) {
      form.setFieldsValue({ selectedStyleId: defaultStyleId });
    }
  }, [stylesRaw, customStyleUnlocked, customStyleMaxLimit, isGuest]);

  // デフォルトスタイルが読み込まれた時にフォームに反映
  useEffect(() => {
    if (defaultStyleId) {
      const cur = form.getFieldValue('selectedStyleId');
      if (!cur) {
        form.setFieldsValue({ selectedStyleId: defaultStyleId });
      }
    }
  }, [defaultStyleId, form]);

  useEffect(() => {
    if (isGuest) return;
    const fetchFeatureStatus = async () => {
      try {
        const profile = await getUserProfile();
        if (profile.role === 'admin' || profile.role === 'master') {
          setStyleAdjustmentUnlocked(true);
          setCustomStyleUnlocked(true);
          setCustomStyleMaxLimit(999);
          return;
        }

        const response = await gamificationApi.getFeaturesStatus();
        const adjustmentStatus = response.features?.style_fine_tuning || {};
        setStyleAdjustmentUnlocked(adjustmentStatus.unlocked || false);
        const planNames = { lite: 'ライト', standard: 'スタンダード', premium: 'プレミアム' };
        setStyleAdjustmentRequiredPlan(
          planNames[adjustmentStatus.required_plan] || adjustmentStatus.required_plan || 'スタンダード',
        );

        const styleStatus = response.features?.custom_style || {};
        setCustomStyleUnlocked(styleStatus.unlocked || false);
        const slotsStatus = response.features?.custom_style_slots || {};
        setCustomStyleMaxLimit(slotsStatus.max_limit ?? 0);
      } catch (e) {
        console.error('Failed to get feature status:', e);
      }
    };
    fetchFeatureStatus();
  }, [isGuest]);

  const sliderMarks = useMemo(
    () => ({
      0: (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          慎重
        </Typography.Text>
      ),
      50: (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          バランス
        </Typography.Text>
      ),
      100: (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          創造的
        </Typography.Text>
      ),
    }),
    [],
  );
  const approachMarks = useMemo(
    () => ({
      0: (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          網羅的
        </Typography.Text>
      ),
      50: (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          バランス
        </Typography.Text>
      ),
      100: (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          深掘り
        </Typography.Text>
      ),
    }),
    [],
  );

  const handleFinish = values => {
    if (isGuest && onGuestAction) {
      onGuestAction();
      return;
    }
    const finalOverrides = styleOverridesRef.current;

    const finalValues = {
      ...values,
      style_overrides: finalOverrides,
    };
    onComplete(finalValues);
  };

  const StyleInfoPanel = ({ stylesRaw, selectedStyleId, isGuest }) => {
    const data = useMemo(() => {
      const found = (stylesRaw || []).find(
        s => String(s.id) === String(selectedStyleId),
      );
      return found?.style_config || null;
    }, [stylesRaw, selectedStyleId]);

    if (!data) {
      if (isGuest && selectedStyleId) {
        return (
          <div style={{ marginTop: 8, padding: '12px 16px', background: '#f6f8fa', borderRadius: 8, fontSize: 13, color: '#666' }}>
            magicss標準のスタイル設定が適用されます。見出し構成・文体・トーンなどが最適化された汎用スタイルです。
          </div>
        );
      }
      return null;
    }

    const hs = data.headings_style || {};
    const ps = data.prose_style || {};
    const tone = (ps && ps.tone) || {};
    const counts = hs.counts || {};
    const lp = hs.length_policy || {};
    const se = ps.sentence_endings || {};
    const ratios = se.ratios_per_100_sentences || {};
    const preferred = (ps.transitions && ps.transitions.preferred) || [];
    const fmt = ps.formatting || {};
    const bulletsRules = fmt.rules?.bullets || {};
    const tablesRules = fmt.rules?.tables || {};

    const h2n = counts.h2_per_article?.typical || '-';
    const h3n = counts.h3_per_h2?.typical || '-';
    const h2h = lp.h2_chars?.typical || '-';
    const h3h = lp.h3_chars?.typical || '-';
    const h2b = lp.h2_body_chars?.typical || '-';
    const h3b = lp.h3_body_chars?.typical || '-';
    const dm = ratios.desu_masu || '-';

    const parseAvg = s => {
      if (!s || typeof s !== 'string') return 0;
      const parts = s.split('-').map(x => Number(String(x).trim()));
      if (parts.length === 1 && !Number.isNaN(parts[0])) return parts[0];
      if (parts.length >= 2) {
        const a = Number.isNaN(parts[0]) ? 0 : parts[0];
        const b = Number.isNaN(parts[1]) ? a : parts[1];
        return (a + b) / 2;
      }
      return 0;
    };
    const formatRange = s => {
      if (!s || typeof s !== 'string') return s;
      const parts = s.split('-');
      if (parts.length === 1) {
        const n = Number(parts[0]);
        return Number.isNaN(n) ? s : n.toLocaleString();
      }
      const a = Number(parts[0]);
      const b = Number(parts[1]);
      const fa = Number.isNaN(a) ? parts[0] : a.toLocaleString();
      const fb = Number.isNaN(b) ? parts[1] : b.toLocaleString();
      return `${fa}-${fb}`;
    };

    const totalPref = (() => {
      const parseMinMax = s => {
        if (!s || typeof s !== 'string') return [0, 0, 0];
        const parts = s.split('-').map(x => Number(String(x).trim()));
        if (parts.length >= 2) {
          const a = Number.isFinite(parts[0]) ? parts[0] : 0;
          const b = Number.isFinite(parts[1]) ? parts[1] : a;
          const lo = Math.min(a, b);
          const hi = Math.max(a, b);
          const mid = (lo + hi) / 2;
          return [lo, hi, mid];
        }
        const v = Number.isFinite(parts[0]) ? parts[0] : 0;
        return [v, v, v];
      };

      const [h2Min, h2Max] = (() => {
        const [lo, hi] = parseMinMax(h2n);
        return [lo, hi];
      })();
      const [h3Min, h3Max, h3Mid] = parseMinMax(h3n);
      const [h2DirectMin, h2DirectMax] = (() => {
        const [lo, hi] = parseMinMax(h2b);
        return [lo, hi];
      })();
      const [h3BodyMin, h3BodyMax, h3BodyMid] = parseMinMax(h3b);

      const ratioProvided = counts?.h2_with_h3_ratio?.typical || '';
      const [ratioMinRaw, ratioMaxRaw] = (() => {
        const [lo, hi] = parseMinMax(ratioProvided);
        return [lo, hi];
      })();
      const clamp01 = v => Math.max(0, Math.min(1, v));
      const ratioMaxUse = ratioProvided ? clamp01(ratioMaxRaw) : 0.8;

      if (!h2Max || !h2DirectMax) return '—';

      // 下限は実運用に寄せて: ratio=1, h3は中点, h3本文は中点
      const estMinBase = h2Min * (h2DirectMin + 1 * h3Mid * h3BodyMid);
      // 上限は ratio 上限 × h3/h3_body 上限
      const estMaxBase =
        h2Max * (h2DirectMax + ratioMaxUse * h3Max * h3BodyMax);

      const minEst = Math.max(0, Math.round(estMinBase / 100) * 100);
      const maxEst = Math.max(minEst, Math.round(estMaxBase / 100) * 100);
      return `${minEst.toLocaleString()}-${maxEst.toLocaleString()}`;
    })();

    const sentenceLenAvg = parseAvg(
      ps?.rhythm?.sentence_length_chars?.avg || '',
    );
    const sentenceLenPct = Math.max(
      0,
      Math.min(100, Math.round((sentenceLenAvg / 80) * 100)),
    );
    const toNum = v => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const clamp = v => Math.max(0, Math.min(100, v));

    // 補助: 見出し側のアサーティブ平均
    const hsTemp = hs?.temperature || {};
    const aH2 = toNum(hsTemp?.assertiveness?.h2);
    const aH3 = toNum(hsTemp?.assertiveness?.h3);
    const assertAvg =
      aH2 == null && aH3 == null
        ? null
        : clamp(Math.round(((aH2 ?? aH3 ?? 0) + (aH3 ?? aH2 ?? 0)) / 2));

    // 1) トーン強度
    const toneIntensity = (() => {
      const direct = toNum(ps?.tone?.intensity ?? ps?.intensity);
      if (direct != null) return clamp(direct);
      if (assertAvg != null) return assertAvg;
      const tf = ps?.transitions?.frequency_per_1000w?.typical;
      if (typeof tf === 'string') {
        const parts = tf.split('-').map(x => Number(x.trim()));
        const avg =
          parts.length >= 2
            ? isNaN(parts[1])
              ? parts[0]
              : (parts[0] + parts[1]) / 2
            : parts[0];
        if (!isNaN(avg)) return clamp(Math.round(avg * 10)); // 0-10 -> 0-100
      }
      return null;
    })();

    // 2) 語彙性（情報密度）
    const vocabDensity = (() => {
      const direct = toNum(
        ps?.rhythm?.info_density ??
          ps?.info_density ??
          data?.rhythm?.info_density,
      );
      if (direct != null) return clamp(direct);
      if (sentenceLenAvg) {
        // 文の長さからの近似: 20〜80字を 80〜20 に線形反転
        const norm = Math.max(20, Math.min(80, sentenceLenAvg));
        const pct = Math.round(80 - ((norm - 20) / 60) * 60) + 20; // 20..80 → 80..20 + 20 = 100..40→丸め
        return clamp(pct);
      }
      return null;
    })();

    // 3) 専門性
    const expertise = (() => {
      const direct = toNum(
        ps?.tone?.expertise ?? ps?.expertise ?? data?.tone?.expertise,
      );
      if (direct != null) return clamp(direct);
      if (assertAvg != null) return assertAvg;
      const neutrality = toNum(
        data?.stance_and_conversion?.media_stance?.neutrality,
      );
      if (neutrality != null) return clamp(100 - neutrality); // 中立性の反転を近似
      return null;
    })();

    return (
      <div>
        <div
          style={{
            marginBottom: 12,
            padding: '8px 12px',
            background: '#f0f5ff',
            borderRadius: 6,
            fontSize: 13,
            color: '#595959',
            lineHeight: 1.6,
          }}
        >
          記事のトーンや構成を変えたい場合は、上から別のスタイルを選択できます。
        </div>
        <div className="style-kpis">
          <div className="style-kpi">
            <div className="label">想定文字数（記事全体）</div>
            <div className="value">
              {totalPref}
              <span className="unit">文字</span>
            </div>
          </div>
          <div className="style-kpi">
            <div className="label">です/ます比率</div>
            <div className="value">
              {formatRange(dm)}
              <span className="unit">%</span>
            </div>
          </div>
        </div>
        <div className="style-sep"></div>

        <div className="style-grid">
          <div className="head">項目</div>
          <div className="head">H2</div>
          <div className="head">H3</div>

          <div className="cell left">
            <div className="metric">基本設置数</div>
          </div>
          <div className="cell">
            <div className="num">
              {formatRange(h2n)}
              <span className="unit">個</span>
            </div>
          </div>
          <div className="cell">
            <div className="num">
              {formatRange(h3n)}
              <span className="unit">個 / H2あたり</span>
            </div>
          </div>

          <div className="cell left">
            <div className="metric">見出し文字数</div>
          </div>
          <div className="cell">
            <div className="num">
              {formatRange(h2h)}
              <span className="unit">文字</span>
            </div>
          </div>
          <div className="cell">
            <div className="num">
              {formatRange(h3h)}
              <span className="unit">文字</span>
            </div>
          </div>

          <div className="cell left">
            <div className="metric">見出し直下の文字数</div>
          </div>
          <div className="cell">
            <div className="num">
              {formatRange(h2b)}
              <span className="unit">文字</span>
            </div>
          </div>
          <div className="cell">
            <div className="num">
              {formatRange(h3b)}
              <span className="unit">文字</span>
            </div>
          </div>
        </div>
        {(() => {
          const b =
            data?.prose_style?.formatting?.bullets?.frequency_per_1000w
              ?.typical ||
            data?.formatting?.bullets?.frequency_per_1000w?.typical;
          const t =
            data?.prose_style?.formatting?.tables?.frequency_per_1000w
              ?.typical ||
            data?.formatting?.tables?.frequency_per_1000w?.typical;
          if (!b && !t) return null;
        })()}

        <div className="style-sep"></div>

        <div className="style-bars">
          <div className="barcard">
            <div className="barlabel">文の長さ</div>
            <Slider
              className="viz-slider"
              disabled
              value={sentenceLenPct}
              tooltip={{ open: false }}
            />
            <div className="barvalue">
              {sentenceLenAvg ? `${Math.round(sentenceLenAvg)} 文字/文` : '—'}
            </div>
          </div>
          <div className="barcard">
            <div className="barlabel">トーン</div>
            <Slider
              className="viz-slider"
              disabled
              value={toneIntensity ?? 0}
              tooltip={{ open: false }}
            />
            <div className="barvalue">
              {toneIntensity != null ? `${toneIntensity}%` : '—'}
            </div>
          </div>
          <div className="barcard">
            <div className="barlabel">語彙性</div>
            <Slider
              className="viz-slider"
              disabled
              value={vocabDensity ?? 0}
              tooltip={{ open: false }}
            />
            <div className="barvalue">
              {vocabDensity != null ? `${vocabDensity}%` : '—'}
            </div>
          </div>
          <div className="barcard">
            <div className="barlabel">専門性</div>
            <Slider
              className="viz-slider"
              disabled
              value={expertise ?? 0}
              tooltip={{ open: false }}
            />
            <div className="barvalue">
              {expertise != null ? `${expertise}%` : '—'}
            </div>
          </div>
        </div>
        <div className="style-sep"></div>
        <div className="style-aux-bars">
          <div className="barcard">
            <div className="barlabel">箇条書き</div>
            <div className="num">
              {fmt?.bullets?.frequency_per_1000w?.typical || '0-0'}
              <span className="unit">/1000字</span>
            </div>
          </div>
          <div className="barcard">
            <div className="barlabel">テーブル</div>
            <div className="num">
              {fmt?.tables?.frequency_per_1000w?.typical || '0-0'}
              <span className="unit">/1000字</span>
            </div>
          </div>
        </div>
        {(() => {
          const desc = data?.metadata?.description || '';
          return desc ? (
            <div
              className="style-summary-card search-intent-card"
              style={{ marginTop: 12 }}
            >
              <div className="style-summary-text search-intent-text">
                {desc}
              </div>
            </div>
          ) : null;
        })()}

        <div className="style-note">
          スタイルはAIが一つの基準とするためのもので、あくまで目安となります
        </div>
      </div>
    );
  };

  return (
    <>
      {/* チュートリアルTour */}
      {!tourLoading && (
        <EnhancedTour
          open={tourOpen}
          steps={tourSteps}
          onClose={() => completeTour()}
          onFinish={() => completeTour()}
          onSkip={() => skipTour()}
          current={currentStep}
          onChange={updateCurrentStep}
          tourTitle="STEP1: 基本設計"
        />
      )}

      {/* TIPS（STEP1用） */}
      <div className="v2-form-narrow" style={{ marginBottom: 16 }}>
        <TipsRotator
          tips={generationStep1Tips}
          intervalMs={7000}
          fadeMs={300}
        />
      </div>

      <div className="v2-form-narrow">
        <Form
          form={form}
          layout="vertical"
          className="v2-form-underline v2-form-spaced"
          colon={false}
          initialValues={{
            selectedAnalysisId: initialValues?.selectedAnalysisId || null,
            keyword: initialValues?.keyword || '',
            selectedStyleId:
              initialValues?.selectedStyleId || defaultStyleId || null,
            selectedToppingIds: initialValues?.selectedToppingIds || [],
            articlePurpose: initialValues?.articlePurpose || '',
            customNotes: initialValues?.customNotes || '',
          }}
          onFinish={handleFinish}
          onValuesChange={onValuesChange}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <Form.Item
                label="ベースにするリサーチ結果を選択"
              >
                <Row gutter={8} align="middle">
                  <Col flex="auto">
                    <Form.Item name="selectedAnalysisId" noStyle>
                      <Select
                        placeholder="リサーチを選択"
                        allowClear
                        showSearch
                        loading={loading.analyses}
                        options={analysisOptions}
                        optionFilterProp="label"
                        filterOption={false}
                        onSearch={handleSearchAnalysis}
                        size="large"
                        bordered={false}
                        style={{ height: 48, borderRadius: 8, fontSize: 16 }}
                        className={
                          !form.getFieldValue('selectedAnalysisId')
                            ? 'blink-border'
                            : ''
                        }
                        onSelect={(val, option) => {
                          const chosen = analysisOptions.find(
                            o => o.value === option?.value,
                          );
                          if (chosen?.keyword) {
                            form.setFieldsValue({ keyword: chosen.keyword });
                          }
                          form.setFieldsValue({ selectedAnalysisId: val });
                          setHasAnalysis(true);
                        }}
                        onChange={val => {
                          if (!val) {
                            form.setFieldsValue({
                              keyword: '',
                              selectedAnalysisId: null,
                            });
                          } else {
                            form.setFieldsValue({ selectedAnalysisId: val });
                          }
                          setHasAnalysis(!!val);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col>
                    <Tooltip title="リサーチ結果の詳細を別タブで開く">
                      <Button
                        icon={<ExportOutlined />}
                        onClick={() => {
                          const id =
                            selectedAnalysisIdWatch ||
                            form.getFieldValue('selectedAnalysisId');
                          if (!id) return;
                          const url = `${window.location.origin}/aio/analysis/${encodeURIComponent(
                            String(id),
                          )}`;
                          openExternal(url);
                        }}
                        disabled={!hasAnalysis}
                      />
                    </Tooltip>
                  </Col>
                </Row>
              </Form.Item>

              <Form.Item
                label="ターゲットキーワード"
                name="keyword"
                rules={[
                  { required: true, message: 'キーワードを入力してください' },
                ]}
              >
                <Input
                  placeholder="例：SEO AEO 違い"
                  bordered={false}
                  style={{ height: 48, borderRadius: 8, fontSize: 16 }}
                />
              </Form.Item>

              <Form.Item
                label={
                  <Space>
                    <span>記事の方針や目的（任意）</span>
                    <Tooltip title="この記事で達成したい目的、ターゲット読者、伝えたいメッセージ、避けたい表現などを自由に記載してください。構成案生成・本文生成・ブラッシュアップの全工程で参照されます。">
                      <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                    </Tooltip>
                  </Space>
                }
                name="articlePurpose"
              >
                <Input.TextArea
                  rows={6}
                  maxLength={500}
                  showCount
                  placeholder={
                    '例：\n・SEO初心者向けに基礎知識を分かりやすく解説する\n・専門用語は極力避け、具体例を多く入れる\n・30代女性をターゲットに共感を意識した文体で書く\n※URLを入力しても参照されません'
                  }
                  bordered={false}
                />
              </Form.Item>

              <Form.Item label="内部用メモ" name="customNotes">
                <Input.TextArea
                  rows={4}
                  placeholder="補足や作業メモ（プロンプトには使用されません）"
                  bordered={false}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Card
                title={
                  <span>
                    スタイル設定{' '}
                    <Tag
                      color="blue"
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        lineHeight: '18px',
                      }}
                    >
                      変更可能
                    </Tag>
                  </span>
                }
                bordered
              >
                <Form.Item
                  label="スタイルを選択"
                  name="selectedStyleId"
                  className="style-select-item"
                >
                  <Select
                    placeholder="スタイルを選択してください"
                    allowClear
                    showSearch
                    loading={loading.styles}
                    options={styleOptions}
                    optionFilterProp="label"
                    size="large"
                  />
                </Form.Item>
                <StyleInfoPanel
                  stylesRaw={stylesRaw}
                  selectedStyleId={selectedStyleIdWatch}
                  isGuest={isGuest}
                />

                {/* 微調整プレビュー */}
                {styleOverrides && Object.keys(styleOverrides).length > 0 && (
                  <Alert
                    type="warning"
                    message="微調整する箇所"
                    description={
                      <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                        {getOverrideSummary(styleOverrides).map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    }
                    showIcon
                    style={{ marginTop: 12 }}
                  />
                )}

                {/* 微調整ボタン */}
                {styleAdjustmentUnlocked ? (
                  <Button
                    block
                    icon={<SettingOutlined />}
                    onClick={() => setStyleOverrideModalOpen(true)}
                    style={{ marginTop: 12 }}
                    disabled={!selectedStyleIdWatch}
                  >
                    今回だけスタイルを微調整する
                  </Button>
                ) : (
                  <Tooltip
                    title={`スタイル微調整機能は${styleAdjustmentRequiredPlan}プラン以上で利用可能です`}
                  >
                    <Button
                      block
                      icon={<LockOutlined />}
                      style={{ marginTop: 12 }}
                      disabled
                    >
                      スタイル微調整（ロック中）
                    </Button>
                  </Tooltip>
                )}
              </Card>
            </Col>
          </Row>

          <div style={{ marginTop: 16 }}>
            <Button
              size="large"
              block
              type="primary"
              onClick={async () => {
                if (isGuest && onGuestAction) {
                  onGuestAction();
                  return;
                }
                try {
                  const values = await form.validateFields();

                  // スタイル設定を取得してクレジット計算
                  const selectedStyle = stylesRaw.find(
                    s => String(s.id) === String(values.selectedStyleId),
                  );
                  const selectedStyleConfig =
                    selectedStyle?.style_config || null;

                  // 🔥 クレジット計算
                  const baseCredits = BASE_GENERATION_CREDITS; // 30
                  const { minCredits, maxCredits, explanation } =
                    calculateEstimatedUsageCredits(
                      selectedStyleConfig,
                      styleOverridesRef.current,
                    );
                  const estimatedMin = calculateTotalCredits(
                    baseCredits,
                    minCredits,
                  );
                  const estimatedMax = calculateTotalCredits(
                    baseCredits,
                    maxCredits,
                  );

                  // 🔥 利用可能クレジットを取得
                  const creditInfo = await creditsApi.getMyCredits();
                  const availableCredits = creditInfo.available || 0;

                  // 🔥 管理者判定（roleベース）
                  const userProfile = await getUserProfile();
                  const isAdmin =
                    userProfile?.role === 'admin' ||
                    userProfile?.role === 'master';

                  // 🔥 クレジット不足チェック（管理者以外）
                  if (!isAdmin && availableCredits < estimatedMin) {
                    // ベースクレジット（30）未満かどうかで分岐
                    const isBaseCreditInsufficient =
                      availableCredits < baseCredits;

                    // Promiseでモーダルの結果を待つ
                    const shouldContinue = await new Promise(resolve => {
                      Modal.confirm({
                        title: 'クレジットが不足しています',
                        icon: (
                          <ExclamationCircleOutlined
                            style={{ color: '#faad14' }}
                          />
                        ),
                        content: (
                          <div>
                            <p>
                              この生成には
                              <strong>
                                {' '}
                                {estimatedMin}〜{estimatedMax} クレジット
                              </strong>
                              が必要な見込みですが、現在
                              <strong> {availableCredits} クレジット</strong>
                              しかありません。
                            </p>
                            <p style={{ marginTop: 12, color: '#666' }}>
                              プランをアップグレードするか、ボーナスクレジットをご購入ください。
                            </p>
                          </div>
                        ),
                        okText: 'プラン管理へ',
                        cancelText: isBaseCreditInsufficient
                          ? 'ボーナスクレジット購入'
                          : 'このまま続ける',
                        okButtonProps: { type: 'primary' },
                        cancelButtonProps: { type: 'default' },
                        onOk: () => {
                          navigate('/subscription-management');
                          resolve(false); // 処理中断
                        },
                        onCancel: () => {
                          if (isBaseCreditInsufficient) {
                            navigate('/bonus-credit');
                            resolve(false); // 処理中断
                          } else {
                            resolve(true); // 「このまま続ける」→処理続行
                          }
                        },
                      });
                    });

                    if (!shouldContinue) {
                      return;
                    }
                    // shouldContinue === true の場合、処理続行
                  }

                  // 🔥 確認モーダルを表示
                  const analysisLabel =
                    analysisOptions.find(
                      opt => opt.value === values.selectedAnalysisId,
                    )?.label || '選択なし';

                  const styleLabel =
                    styleOptions.find(
                      opt => opt.value === values.selectedStyleId,
                    )?.label || '-';

                  // スタイルオーバーライドのサマリーを取得
                  const styleOverrideSummary = getOverrideSummary(
                    styleOverridesRef.current,
                  );

                  setConfirmData({
                    values,
                    analysisLabel,
                    styleLabel,
                    styleOverrideSummary,
                    baseCredits,
                    minCredits,
                    maxCredits,
                    estimatedMin,
                    estimatedMax,
                    availableCredits,
                    isAdmin,
                    explanation,
                  });
                  setConfirmModalVisible(true);
                } catch (error) {
                  // validation errors handled by antd
                  console.error('Validation failed:', error);
                }
              }}
            >
              {isGuest ? '次へ（ログインが必要です）' : '次へ'}
            </Button>
          </div>
        </Form>
      </div>
      <Modal
        title="独自情報を選択（記事全体にブレンド）"
        open={toppingModalOpen}
        onCancel={() => setToppingModalOpen(false)}
        footer={null}
        width={900}
      >
        <Typography.Paragraph style={{ color: '#666' }}>
          選択した独自情報は、本文全体に“ブレンド”される形で参照されます。
        </Typography.Paragraph>
        <Transfer
          dataSource={toppingOptions.map(o => ({
            key: String(o.value),
            title: o.label,
            description:
              `${o.type ? `[${o.type}]` : ''} ${o.category ? `[${o.category}]` : ''}`.trim(),
          }))}
          showSearch
          filterOption={(input, item) =>
            item.title.toLowerCase().includes(input.toLowerCase()) ||
            (item.description || '').toLowerCase().includes(input.toLowerCase())
          }
          listStyle={{ width: '45%', height: 260 }}
          render={item => (
            <Space size={4}>
              {item.description && (
                <Tag color="#d3adf7">{item.description}</Tag>
              )}
              <span>{item.title}</span>
            </Space>
          )}
          targetKeys={toppingTargetKeys}
          onChange={nextTargetKeys => {
            // antd Transfer はkey配列を返す。フォームにそのまま反映
            form.setFieldsValue({ selectedToppingIds: nextTargetKeys });
            setToppingTargetKeys(nextTargetKeys);
          }}
          titles={[
            <Typography.Text key="l" type="secondary">
              候補
            </Typography.Text>,
            <Typography.Text key="r" type="secondary">
              選択済み
            </Typography.Text>,
          ]}
        />
      </Modal>

      {/* スタイル微調整モーダル */}
      <StyleOverrideModal
        visible={styleOverrideModalOpen}
        onCancel={() => setStyleOverrideModalOpen(false)}
        onSave={handleSaveStyleOverrides}
        baseStyle={
          stylesRaw.find(s => s.id === selectedStyleIdWatch)?.style_config
        }
        styleName={
          stylesRaw.find(s => s.id === selectedStyleIdWatch)?.name || 'スタイル'
        }
        currentOverrides={styleOverrides}
      />

      {/* 🔥 クレジット確認モーダル */}
      <Modal
        title="生成の確認"
        open={confirmModalVisible}
        onCancel={() => {
          setConfirmModalVisible(false);
          setConfirmData(null);
        }}
        footer={
          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
            <Button
              key="cancel"
              onClick={() => {
                setConfirmModalVisible(false);
                setConfirmData(null);
              }}
              style={{ flex: '3' }}
            >
              キャンセル
            </Button>
            <Button
              key="ok"
              type="primary"
              loading={confirming}
              onClick={async () => {
                if (!confirmData) return;
                try {
                  setConfirming(true);
                  // style_overridesを追加してonCompleteを呼び出し
                  const finalValues = {
                    ...confirmData.values,
                    style_overrides: styleOverridesRef.current,
                  };
                  await onComplete(finalValues);
                } catch (error) {
                  console.error('❌ [Step1] Failed to complete:', error);
                  message.error('生成の開始に失敗しました');
                } finally {
                  setConfirming(false);
                  setConfirmModalVisible(false);
                  setConfirmData(null);
                }
              }}
              style={{ flex: '7' }}
            >
              生成を開始する
              <FireOutlined style={{ marginLeft: 8 }} /> 30
            </Button>
          </div>
        }
        width={640}
        maskClosable={false}
      >
        {confirmData && (
          <div>
            {/* 設定内容の確認 */}
            {!confirmData.values?.selectedAnalysisId && (
              <div
                style={{
                  background: '#fff7e6',
                  border: '1px solid #ffd591',
                  borderRadius: '6px',
                  padding: '12px 16px',
                  marginBottom: '16px',
                  color: '#ad6800',
                  fontSize: '13px',
                  lineHeight: '1.6',
                }}
              >
                <strong>⚠️ リサーチ結果が選択されていません</strong>
                <br />
                簡易的に分析をした上で構成案を生成しますが、リサーチ結果を含める場合と比較すると品質が劣ります。
              </div>
            )}

            <Descriptions
              bordered
              size="small"
              column={1}
              labelStyle={{ width: 176, fontSize: 13 }}
              contentStyle={{ fontSize: 13 }}
              style={{ marginBottom: 20 }}
            >
              <Descriptions.Item label="ベースにするリサーチ">
                {confirmData.analysisLabel}
              </Descriptions.Item>
              <Descriptions.Item label="ターゲットキーワード">
                {confirmData.values?.keyword || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="スタイル">
                <div>
                  {confirmData.styleLabel}
                  {confirmData.styleOverrideSummary &&
                    confirmData.styleOverrideSummary.length > 0 && (
                      <div
                        style={{ marginTop: 6, fontSize: 12, color: '#8c8c8c' }}
                      >
                        <div style={{ fontWeight: 500 }}>【微調整】</div>
                        {confirmData.styleOverrideSummary.map((item, idx) => (
                          <div key={idx}>• {item}</div>
                        ))}
                      </div>
                    )}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="記事の方針や目的">
                {confirmData.values?.articlePurpose || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="内部用メモ">
                {confirmData.values?.customNotes || '-'}
              </Descriptions.Item>
            </Descriptions>

            {/* クレジット内訳 */}
            <div style={{ marginBottom: 16, fontSize: 13 }}>
              {/* ベースクレジット */}
              <Row
                justify="space-between"
                align="middle"
                style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}
              >
                <Col>
                  <span>ベースクレジット（固定）</span>
                </Col>
                <Col style={{ textAlign: 'center' }}>
                  <span style={{ fontWeight: 500, color: '#1890ff' }}>
                    <FireOutlined style={{ marginRight: 4 }} />
                    {confirmData.baseCredits}
                  </span>
                </Col>
              </Row>

              {/* 従量クレジット */}
              <Row
                justify="space-between"
                align="middle"
                style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}
              >
                <Col>
                  <span>従量クレジット概算（本文生成時に確定・消費）</span>
                </Col>
                <Col style={{ textAlign: 'center' }}>
                  <span style={{ fontWeight: 500, color: '#8c8c8c' }}>
                    <FireOutlined style={{ marginRight: 4 }} />
                    {confirmData.minCredits}~{confirmData.maxCredits}
                  </span>
                </Col>
              </Row>

              {/* 合計クレジット */}
              <Row
                justify="space-between"
                align="middle"
                style={{ padding: '8px 0' }}
              >
                <Col>
                  <span style={{ fontSize: 14 }}>合計クレジット（概算）</span>
                </Col>
                <Col style={{ textAlign: 'center' }}>
                  <span
                    style={{ fontWeight: 500, fontSize: 14, color: '#8c8c8c' }}
                  >
                    <FireOutlined style={{ marginRight: 4 }} />
                    {confirmData.estimatedMin}~{confirmData.estimatedMax}
                  </span>
                </Col>
              </Row>
            </div>

            {/* 利用可能クレジット */}
            <div
              style={{
                padding: '10px 12px',
                background: '#f6ffed',
                borderRadius: 4,
                marginBottom: 16,
                fontSize: 13,
              }}
            >
              <Row justify="space-between" align="middle">
                <Col>
                  <span>利用可能クレジット</span>
                </Col>
                <Col style={{ textAlign: 'center' }}>
                  <strong style={{ color: '#52c41a', fontSize: 14 }}>
                    {confirmData.isAdmin ? (
                      '∞'
                    ) : (
                      <>
                        <FireOutlined style={{ marginRight: 4 }} />
                        {confirmData.availableCredits.toLocaleString()}
                      </>
                    )}
                  </strong>
                </Col>
              </Row>
            </div>

            {/* 注意事項 */}
            <div style={{ fontSize: 12, color: '#8c8c8c', lineHeight: '1.6' }}>
              ※ ベースクレジットはこの後すぐに消費されます
              <br />
              ※ 従量クレジットは本文生成時に確定・消費されます
              <br />※
              上記は概算値です。実際の消費クレジットは構成内容により変動します
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default Step1BasicDesign;
